import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "react-query";
import { Card, Row, Col, Form, Button, Table, Badge } from "react-bootstrap";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
} from "chart.js";
import { Bar, Doughnut, Pie } from "react-chartjs-2";
import {
  getDashboardSummaryReport,
  getRegionStatsReport,
  getAssessmentSummaryReport,
  getDistricts,
  getSubDistricts,
} from "../../services/dashboardService";

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  Title
);

const Reports = () => {
  const [selectedReport, setSelectedReport] = useState("summary");
  const [selectedRegion, setSelectedRegion] = useState("");
  const [selectedLevel, setSelectedLevel] = useState("kecamatan");
  const [assessmentLevel, setAssessmentLevel] = useState("kecamatan");
  const [selectedDistrict, setSelectedDistrict] = useState("");
  const queryClient = useQueryClient();

  const { data: summaryData, isLoading: summaryLoading } = useQuery(
    ["dashboardSummary"],
    getDashboardSummaryReport
  );

  const { data: regionStats, isLoading: regionLoading } = useQuery(
    ["regionStats", selectedLevel],
    () => getRegionStatsReport(selectedLevel)
  );

  const { data: assessmentData, isLoading: assessmentLoading } = useQuery(
    ["assessmentSummary", assessmentLevel, selectedRegion],
    () => getAssessmentSummaryReport(assessmentLevel, selectedRegion)
  );

  const { data: districts = [] } = useQuery(["districts"], getDistricts, {
    staleTime: 1000 * 60 * 5, // cache 5 menit
    cacheTime: 1000 * 60 * 10, // simpan di memori 10 menit
  });

  const { data: subDistricts = [] } = useQuery(
    ["subDistricts", selectedDistrict],
    () => getSubDistricts(selectedDistrict),
    {
      enabled: assessmentLevel === "desa" && !!selectedDistrict,
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 10,
    }
  );

  useEffect(() => {
    if (assessmentLevel === "desa") {
      districts.forEach((d) => {
        queryClient.prefetchQuery(["subDistricts", d.id], () =>
          getSubDistricts(d.id)
        );
      });
    }
  }, [assessmentLevel, districts, queryClient]);

  const isLoading = summaryLoading || regionLoading || assessmentLoading;

  // console.log("BO DEBUGGING----------");
  // console.log(summaryData);
  // console.log(regionStats);
  // console.log(assessmentData);
  // console.log("EO DEBUGGING-----------");

  const renderSummaryReport = () => (
    <Row>
      <Col md={6}>
        <Card className="mb-4">
          <Card.Header>
            <h5>Distribusi Jenis Kelamin</h5>
          </Card.Header>
          <Card.Body>
            {summaryData?.gender_distribution && (
              <Doughnut
                style={{ height: "300px" }}
                data={{
                  labels: Object.keys(summaryData.gender_distribution),
                  datasets: [
                    {
                      data: Object.values(summaryData.gender_distribution),
                      backgroundColor: [
                        "rgba(0, 123, 255, 0.6)",
                        "rgba(220, 53, 69, 0.6)",
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "bottom",
                    },
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={6}>
        <Card className="mb-4">
          <Card.Header>
            <h5>Distribusi Pendidikan</h5>
          </Card.Header>
          <Card.Body>
            {summaryData?.education_distribution && (
              <Pie
                style={{ height: "300px" }}
                data={{
                  labels: Object.keys(summaryData.education_distribution),
                  datasets: [
                    {
                      data: Object.values(summaryData.education_distribution),
                      backgroundColor: [
                        "rgba(0, 123, 255, 0.6)",
                        "rgba(40, 167, 69, 0.6)",
                        "rgba(255, 193, 7, 0.6)",
                        "rgba(220, 53, 69, 0.6)",
                        "rgba(111, 66, 193, 0.6)",
                        "rgba(253, 126, 20, 0.6)",
                        "rgba(32, 201, 151, 0.6)",
                        "rgba(232, 62, 140, 0.6)",
                      ],
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "bottom",
                    },
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col md={12}>
        <Card className="mb-4">
          <Card.Header>
            <h5>Distribusi Penghasilan</h5>
          </Card.Header>
          <Card.Body>
            {summaryData?.income_distribution && (
              <Bar
                style={{ height: "300px" }}
                data={{
                  labels: Object.keys(summaryData.income_distribution),
                  datasets: [
                    {
                      label: "Jumlah KK",
                      data: Object.values(summaryData.income_distribution),
                      backgroundColor: "rgba(0, 123, 255, 0.6)",
                      borderColor: "rgba(0, 86, 179, 0.6)",
                      borderWidth: 1,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      display: false,
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                    },
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderDistricalReport = () => (
    <Row>
      <Col md={12}>
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5>
                Statistik per{" "}
                {selectedLevel === "kecamatan"
                  ? "Kecamatan"
                  : "Desa / Kelurahan"}
              </h5>
              <Form.Select
                value={selectedLevel}
                onChange={(e) => setSelectedLevel(e.target.value)}
                style={{ width: "200px" }}
                size="sm"
              >
                <option value="kecamatan">Per Kecamatan</option>
                <option value="desa">Per Desa / Kelurahan</option>
                <option value="rw">Per RW</option>
                <option value="rt">Per RT</option>
              </Form.Select>
            </div>
          </Card.Header>

          <Card.Body>
            <Table responsive striped>
              <thead>
                <tr>
                  <th>
                    {selectedLevel === "kecamatan"
                      ? "Kecamatan"
                      : selectedLevel === "desa"
                      ? "Desa / Kelurahan"
                      : selectedLevel.toUpperCase()}
                  </th>
                  <th>Total Rumah</th>
                  <th>Layak Huni</th>
                  <th>Kurang Layak Huni</th>
                  <th>Tidak Layak Huni</th>
                  <th>Rata-rata Skor</th>
                </tr>
              </thead>
              <tbody>
                {regionStats?.map((region, index) => (
                  <tr key={index}>
                    <td>{region.region}</td>
                    <td>{region.total_houses}</td>
                    <td>
                      <Badge className="px-3 py-2" bg="success">
                        {region.layak_huni}
                      </Badge>
                    </td>
                    <td>
                      <Badge className="px-3 py-2" bg="warning">
                        {region.kurang_layak_huni}
                      </Badge>
                    </td>
                    <td>
                      <Badge className="px-3 py-2" bg="danger">
                        {region.tidak_layak_huni}
                      </Badge>
                    </td>
                    <td>
                      <strong>{region.average_score?.toFixed(1) || "-"}</strong>
                    </td>
                    {/* <td>
                        <strong>
                          {typeof region.average_score === "number"
                            ? region.average_score.toFixed(1)
                            : "-"}
                        </strong>
                    </td> */}
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      </Col>

      <Col md={12}>
        <Card>
          <Card.Header>
            <h5>Grafik Statistik per Kecamatan</h5>
          </Card.Header>
          <Card.Body>
            {regionStats && (
              <Bar
                data={{
                  labels: regionStats.map((r) => r.region),
                  datasets: [
                    {
                      label: "Layak Huni",
                      data: regionStats.map((r) => r.layak_huni),
                      backgroundColor: "rgba(40, 167, 69, 0.6)",
                    },
                    {
                      label: "Kurang Layak Huni",
                      data: regionStats.map((r) => r.kurang_layak_huni),
                      backgroundColor: "rgba(255, 193, 7, 0.6)",
                    },
                    {
                      label: "Tidak Layak Huni",
                      data: regionStats.map((r) => r.tidak_layak_huni),
                      backgroundColor: "rgba(220, 53, 69, 0.6)",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "top",
                    },
                  },
                  scales: {
                    x: {
                      stacked: true,
                    },
                    y: {
                      stacked: true,
                      beginAtZero: true,
                    },
                  },
                }}
              />
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderAssessmentReport = () => (
    <Row>
      <Col md={12}>
        <Card className="mb-4">
          <Card.Header>
            <div className="d-flex justify-content-between align-items-center">
              <h5>Laporan Penilaian Kelayakan Huni</h5>
              <div className="d-flex gap-2">
                <Form.Select
                  value={assessmentLevel}
                  onChange={(e) => {
                    setAssessmentLevel(e.target.value);
                    setSelectedRegion("");
                    setSelectedDistrict("");
                  }}
                  style={{ width: "200px" }}
                  size="sm"
                >
                  <option value="kecamatan">Per Kecamatan</option>
                  <option value="desa">Per Desa / Kelurahan</option>
                </Form.Select>

                {assessmentLevel === "kecamatan" ? (
                  <Form.Select
                    value={selectedRegion}
                    onChange={(e) => setSelectedRegion(e.target.value)}
                    style={{ width: "200px" }}
                    size="sm"
                  >
                    <option value="">Semua Kecamatan</option>
                    {districts.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.name}
                      </option>
                    ))}
                  </Form.Select>
                ) : (
                  <>
                    {/* Dropdown pilih kecamatan dulu */}
                    <Form.Select
                      value={selectedDistrict}
                      onChange={(e) => {
                        setSelectedDistrict(e.target.value);
                        setSelectedRegion(""); // reset sub-district kalau ganti kecamatan
                      }}
                      style={{ width: "200px" }}
                      size="sm"
                    >
                      <option value="">Pilih Kecamatan</option>
                      {districts.map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.name}
                        </option>
                      ))}
                    </Form.Select>

                    {/* Dropdown pilih desa */}
                    <Form.Select
                      value={selectedRegion}
                      onChange={(e) => setSelectedRegion(e.target.value)}
                      style={{ width: "200px" }}
                      size="sm"
                      disabled={!selectedDistrict}
                    >
                      <option value="">Semua Desa</option>
                      {subDistricts.map((sd) => (
                        <option key={sd.id} value={sd.id}>
                          {sd.name}
                        </option>
                      ))}
                    </Form.Select>
                  </>
                )}
              </div>
            </div>
          </Card.Header>

          <Card.Body>
            {assessmentData && (
              <div>
                <Row>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-success">
                        {assessmentData.total_assessed}
                      </h3>
                      <p>Total Dinilai</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-success">
                        {assessmentData.layak_huni}
                      </h3>
                      <p>Layak Huni</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-warning">
                        {assessmentData.kurang_layak_huni}
                      </h3>
                      <p>Kurang Layak Huni</p>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-center">
                      <h3 className="text-danger">
                        {assessmentData.tidak_layak_huni}
                      </h3>
                      <p>Tidak Layak Huni</p>
                    </div>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col md={4}>
                    <h6>Distribusi Skor Kelayakan</h6>
                    <Doughnut
                      data={{
                        labels: [
                          "Layak Huni",
                          "Kurang Layak Huni",
                          "Tidak Layak Huni",
                        ],
                        datasets: [
                          {
                            data: [
                              assessmentData.layak_huni,
                              assessmentData.kurang_layak_huni,
                              assessmentData.tidak_layak_huni,
                            ],
                            backgroundColor: [
                              "rgba(40, 167, 69, 0.6)",
                              "rgba(255, 193, 7, 0.6)",
                              "rgba(220, 53, 69, 0.6)",
                            ],
                            borderWidth: 1,
                          },
                        ],
                      }}
                      options={{
                        responsive: true,
                        plugins: {
                          legend: {
                            position: "bottom",
                          },
                        },
                      }}
                    />
                  </Col>
                  <Col md={6}>
                    <h6>Rata-rata Skor per Kecamatan</h6>
                    {assessmentData.average_scores && (
                      <Bar
                        data={{
                          labels: assessmentData.average_scores.map(
                            (item) => item.region
                          ),
                          datasets: [
                            {
                              label: "Rata-rata Skor",
                              data: assessmentData.average_scores.map(
                                (item) => item.average_score
                              ),
                              backgroundColor: "rgba(0, 123, 255, 0.6)",
                              borderColor: "rgba(0, 86, 179, 0.6)",
                              borderWidth: 1,
                            },
                          ],
                        }}
                        options={{
                          responsive: true,
                          plugins: {
                            legend: {
                              display: false,
                            },
                          },
                          scales: {
                            y: {
                              beginAtZero: true,
                              max: 100,
                            },
                          },
                        }}
                      />
                    )}
                  </Col>
                </Row>
              </div>
            )}
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );

  const renderReportContent = () => {
    switch (selectedReport) {
      case "summary":
        return renderSummaryReport();
      case "districal":
        return renderDistricalReport();
      case "assessment":
        return renderAssessmentReport();
      default:
        return renderSummaryReport();
    }
  };

  if (isLoading)
    return <div className="loading-spinner">Loading laporan...</div>;

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Laporan dan Statistik</h2>
        <div className="d-flex gap-2">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() => window.print()}
          >
            <i className="fas fa-print me-2"></i>
            Cetak Laporan
          </Button>
        </div>
      </div>

      <Card className="mb-4">
        <Card.Body>
          <div className="d-flex gap-3">
            <Button
              variant={
                selectedReport === "summary" ? "primary" : "outline-primary"
              }
              onClick={() => setSelectedReport("summary")}
            >
              Ringkasan Umum
            </Button>
            <Button
              variant={
                selectedReport === "districal" ? "primary" : "outline-primary"
              }
              onClick={() => setSelectedReport("districal")}
            >
              Laporan Districal
            </Button>
            <Button
              variant={
                selectedReport === "assessment" ? "primary" : "outline-primary"
              }
              onClick={() => setSelectedReport("assessment")}
            >
              Laporan Penilaian
            </Button>
          </div>
        </Card.Body>
      </Card>

      {renderReportContent()}
    </div>
  );
};

export default Reports;
